import { useEffect, useMemo, useState } from "react"

type UseTypewriterOptions = {
  typeSpeedMs?: number
  deleteSpeedMs?: number
  pauseMs?: number
}

export function useTypewriter(
  phrases: string[],
  { typeSpeedMs = 45, deleteSpeedMs = 22, pauseMs = 900 }: UseTypewriterOptions = {}
) {
  const safePhrases = useMemo(() => phrases.filter(Boolean), [phrases])
  const prefersReducedMotion = useMemo(() => {
    try {
      return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false
    } catch {
      return false
    }
  }, [])

  const [phraseIndex, setPhraseIndex] = useState(0)
  const [subIndex, setSubIndex] = useState(0)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!safePhrases.length) return
    if (prefersReducedMotion) return

    const current = safePhrases[phraseIndex % safePhrases.length]

    // Pause at end of typing
    if (!deleting && subIndex === current.length) {
      const t = window.setTimeout(() => setDeleting(true), pauseMs)
      return () => window.clearTimeout(t)
    }

    // Pause at end of deleting
    if (deleting && subIndex === 0) {
      const t = window.setTimeout(() => {
        setDeleting(false)
        setPhraseIndex((i) => (i + 1) % safePhrases.length)
      }, 250)
      return () => window.clearTimeout(t)
    }

    const stepMs = deleting ? deleteSpeedMs : typeSpeedMs
    const t = window.setTimeout(() => {
      setSubIndex((i) => i + (deleting ? -1 : 1))
    }, stepMs)

    return () => window.clearTimeout(t)
  }, [safePhrases, phraseIndex, subIndex, deleting, typeSpeedMs, deleteSpeedMs, pauseMs, prefersReducedMotion])

  if (!safePhrases.length) return ""
  if (prefersReducedMotion) return safePhrases[0]

  const current = safePhrases[phraseIndex % safePhrases.length]
  return current.substring(0, subIndex)
}

